import { isHorizontal, outwardSign } from "./stub.js";
//#region src/utils/draw-relationship/search.ts
var SEARCH_MARGIN = 240;
var MAX_SEARCH_POINTS = 4096;
var STEP_X = [
	1,
	-1,
	0,
	0
];
var STEP_Y = [
	0,
	0,
	1,
	-1
];
function headingOf(direction, sign = 1) {
	const outward = outwardSign(direction) * sign;
	if (isHorizontal(direction)) return outward > 0 ? 0 : 1;
	return outward > 0 ? 2 : 3;
}
var pointBlocked = /* @__PURE__ */ new Uint8Array(0);
var acrossBlocked = /* @__PURE__ */ new Uint8Array(0);
var downBlocked = /* @__PURE__ */ new Uint8Array(0);
var spentTo = /* @__PURE__ */ new Float64Array(0);
var cameFrom = /* @__PURE__ */ new Int32Array(0);
function reserve(points) {
	if (pointBlocked.length < points) {
		pointBlocked = new Uint8Array(points);
		acrossBlocked = new Uint8Array(points);
		downBlocked = new Uint8Array(points);
		spentTo = new Float64Array(points * 4);
		cameFrom = new Int32Array(points * 4);
	}
	pointBlocked.fill(0, 0, points);
	acrossBlocked.fill(0, 0, points);
	downBlocked.fill(0, 0, points);
	spentTo.fill(Infinity, 0, points * 4);
	cameFrom.fill(-1, 0, points * 4);
}
function uniqueSorted(values) {
	values.sort((a, b) => a - b);
	const out = [];
	for (const value of values) {
		if (out.length && value - out[out.length - 1] < .5) continue;
		out.push(value);
	}
	return out;
}
function lowerBound(sorted, value) {
	let low = 0;
	let high = sorted.length;
	while (low < high) {
		const middle = low + high >> 1;
		if (sorted[middle] < value) low = middle + 1;
		else high = middle;
	}
	return low;
}
function upperBound(sorted, value) {
	let low = 0;
	let high = sorted.length;
	while (low < high) {
		const middle = low + high >> 1;
		if (sorted[middle] <= value) low = middle + 1;
		else high = middle;
	}
	return low;
}
function createQueue() {
	const costs = [];
	const states = [];
	const less = (a, b) => costs[a] < costs[b] || costs[a] === costs[b] && states[a] < states[b];
	const swap = (a, b) => {
		[costs[a], costs[b]] = [costs[b], costs[a]];
		[states[a], states[b]] = [states[b], states[a]];
	};
	return {
		get size() {
			return costs.length;
		},
		push(cost, state) {
			costs.push(cost);
			states.push(state);
			let index = costs.length - 1;
			while (index > 0) {
				const parent = index - 1 >> 1;
				if (!less(index, parent)) break;
				swap(index, parent);
				index = parent;
			}
		},
		pop() {
			const top = [costs[0], states[0]];
			const lastCost = costs.pop();
			const lastState = states.pop();
			if (costs.length) {
				costs[0] = lastCost;
				states[0] = lastState;
				let index = 0;
				for (;;) {
					const left = index * 2 + 1;
					const right = left + 1;
					let next = index;
					if (left < costs.length && less(left, next)) next = left;
					if (right < costs.length && less(right, next)) next = right;
					if (next === index) break;
					swap(index, next);
					index = next;
				}
			}
			return top;
		}
	};
}
function searchRoute(m, mDirection, l, lDirection, obstacles, startTableId, endTableId, clearance, bendCost) {
	const region = {
		left: Math.min(m.x, l.x) - SEARCH_MARGIN,
		top: Math.min(m.y, l.y) - SEARCH_MARGIN,
		right: Math.max(m.x, l.x) + SEARCH_MARGIN,
		bottom: Math.max(m.y, l.y) + SEARCH_MARGIN
	};
	const inside = [];
	const xs = [
		m.x,
		l.x,
		region.left,
		region.right
	];
	const ys = [
		m.y,
		l.y,
		region.top,
		region.bottom
	];
	for (let index = 0; index < obstacles.ids.length; index++) {
		if (obstacles.right[index] < region.left || obstacles.left[index] > region.right || obstacles.bottom[index] < region.top || obstacles.top[index] > region.bottom) continue;
		inside.push(index);
		xs.push(obstacles.left[index] - clearance, obstacles.right[index] + clearance);
		ys.push(obstacles.top[index] - clearance, obstacles.bottom[index] + clearance);
	}
	const nearEnd = (value, a, b) => value !== a && value !== b && (Math.abs(value - a) < 1 || Math.abs(value - b) < 1);
	const columns = uniqueSorted(xs.filter((x) => x >= region.left && x <= region.right && !nearEnd(x, m.x, l.x)));
	const rows = uniqueSorted(ys.filter((y) => y >= region.top && y <= region.bottom && !nearEnd(y, m.y, l.y)));
	const width = columns.length;
	const height = rows.length;
	if (width * height > MAX_SEARCH_POINTS) return {
		points: null,
		region
	};
	reserve(width * height);
	const point = pointBlocked;
	const across = acrossBlocked;
	const down = downBlocked;
	for (const index of inside) {
		const left = obstacles.left[index];
		const right = obstacles.right[index];
		const top = obstacles.top[index];
		const bottom = obstacles.bottom[index];
		const firstColumn = lowerBound(columns, left);
		const firstRow = lowerBound(rows, top);
		for (let row = firstRow; row < height && rows[row] <= bottom; row++) for (let column = Math.max(0, firstColumn - 1); column < width && columns[column] <= right; column++) {
			const at = row * width + column;
			if (columns[column] >= left) point[at] = 1;
			if (column + 1 < width && columns[column + 1] >= left) across[at] = 1;
		}
		for (let column = firstColumn; column < width && columns[column] <= right; column++) for (let row = Math.max(0, firstRow - 1); row < height && rows[row] <= bottom; row++) if (row + 1 < height && rows[row + 1] >= top) down[row * width + column] = 1;
	}
	const rowOf = (y) => upperBound(rows, y - .5);
	const columnOf = (x) => upperBound(columns, x - .5);
	const start = rowOf(m.y) * width + columnOf(m.x);
	const goal = rowOf(l.y) * width + columnOf(l.x);
	if (point[start] || point[goal]) return {
		points: null,
		region
	};
	const close = (at) => {
		if (at !== start && at !== goal) point[at] = 1;
	};
	const closeStub = (end, direction, tableId) => {
		const index = obstacles.ids.indexOf(tableId);
		if (index < 0) return;
		const sign = outwardSign(direction);
		if (isHorizontal(direction)) {
			const edge = sign > 0 ? obstacles.right[index] : obstacles.left[index];
			const row = rowOf(end.y);
			for (let column = 0; column < width; column++) {
				const x = columns[column];
				if ((x - edge) * sign > 0 && (end.x - x) * sign > .5) close(row * width + column);
			}
		} else {
			const edge = sign > 0 ? obstacles.bottom[index] : obstacles.top[index];
			const column = columnOf(end.x);
			for (let row = 0; row < height; row++) {
				const y = rows[row];
				if ((y - edge) * sign > 0 && (end.y - y) * sign > .5) close(row * width + column);
			}
		}
	};
	closeStub(m, mDirection, startTableId);
	closeStub(l, lDirection, endTableId);
	const startHeading = headingOf(mDirection);
	const away = headingOf(lDirection);
	const arrive = headingOf(lDirection, -1);
	const best = spentTo;
	const parent = cameFrom;
	const queue = createQueue();
	const estimate = (at) => Math.abs(columns[at % width] - l.x) + Math.abs(rows[Math.floor(at / width)] - l.y);
	const first = start * 4 + startHeading;
	best[first] = 0;
	queue.push(estimate(start), first);
	let found = -1;
	let foundCost = Infinity;
	while (queue.size) {
		const [cost, state] = queue.pop();
		if (cost >= foundCost) break;
		const at = state >> 2;
		const heading = state & 3;
		const spent = best[state];
		if (cost > spent + estimate(at)) continue;
		if (at === goal) {
			const total = spent + (heading === arrive ? 0 : bendCost);
			if (heading !== away && total < foundCost) {
				foundCost = total;
				found = state;
			}
			continue;
		}
		const column = at % width;
		const row = (at - column) / width;
		for (let next = 0; next < 4; next++) {
			if (STEP_X[next] === -STEP_X[heading] && STEP_Y[next] === -STEP_Y[heading]) continue;
			const nextColumn = column + STEP_X[next];
			const nextRow = row + STEP_Y[next];
			if (nextColumn < 0 || nextColumn >= width) continue;
			if (nextRow < 0 || nextRow >= height) continue;
			const target = nextRow * width + nextColumn;
			if (point[target]) continue;
			if (next < 2 && across[row * width + Math.min(column, nextColumn)]) continue;
			if (next >= 2 && down[Math.min(row, nextRow) * width + column]) continue;
			const total = spent + (Math.abs(columns[nextColumn] - columns[column]) + Math.abs(rows[nextRow] - rows[row])) + (next === heading ? 0 : bendCost);
			const nextState = target * 4 + next;
			if (total >= best[nextState]) continue;
			best[nextState] = total;
			parent[nextState] = state;
			queue.push(total + estimate(target), nextState);
		}
	}
	if (found < 0) return {
		points: null,
		region
	};
	const path = [];
	for (let state = found; state >= 0; state = parent[state]) {
		const at = state >> 2;
		path.push({
			x: columns[at % width],
			y: rows[Math.floor(at / width)]
		});
	}
	path.reverse();
	path[0] = {
		x: m.x,
		y: m.y
	};
	path[path.length - 1] = {
		x: l.x,
		y: l.y
	};
	return {
		points: straighten(path),
		region
	};
}
function straighten(points) {
	const out = [];
	for (const current of points) {
		const last = out[out.length - 1];
		if (last && Math.abs(last.x - current.x) < .5 && Math.abs(last.y - current.y) < .5) continue;
		if (out.length >= 2) {
			const before = out[out.length - 2];
			const upright = Math.abs(before.x - last.x) < .5 && Math.abs(last.x - current.x) < .5;
			const level = Math.abs(before.y - last.y) < .5 && Math.abs(last.y - current.y) < .5;
			if (upright || level) {
				out[out.length - 1] = current;
				continue;
			}
		}
		out.push(current);
	}
	return out;
}
//#endregion
export { searchRoute };
